import AddIcon from "@mui/icons-material/Add";
import { Autocomplete, CircularProgress, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FormikErrors, useFormikContext } from "formik";
import { useCallback, useEffect, useState } from "react";

import MDInput from "@/components/atoms/MDInput";
import { CampaignFormData } from "@/components/screens/CampaignFormPage/types";
import useGetProducts from "@/hooks/requests/product/use-get-products";
import useDialog from "@/hooks/use-dialog";
import useSelectSearch from "@/hooks/use-select-search";
import { ICampaignItem } from "@/interfaces/campaign.interface";
import IProduct from "@/interfaces/product.interface";
import { CampaignFormPageMessages } from "@/messages/campaigns/campaign-form-page.messages";
import { FilterType, IFilter } from "@/types/filter.types";

type SetFieldValue = (
  field: string,
  value: unknown,
  shouldValidate?: boolean
) => Promise<FormikErrors<CampaignFormData> | void>;

type ConfirmHandler = (product: IProduct | null, setFieldValue: SetFieldValue) => void;

interface Props {
  messages: CampaignFormPageMessages;
  onConfirm: ConfirmHandler;
  items: ICampaignItem[] | undefined;
}

const pageNumber = 0;
const pageSize = 50;

const ProductAutoComplete = ({ onConfirm, items, messages }: Props) => {
  const { setFieldValue } = useFormikContext<CampaignFormData>();
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const { isVisible: open, handleClose, handleOpen } = useDialog();
  const useSearch = useSelectSearch<IProduct>();
  const filterType = FilterType.Contains;
  const property = "code";
  const filters = [{ property, filterType, value: useSearch.search }] as IFilter<IProduct>[];
  const { isLoading: isLoadingRequest, data } = useGetProducts({ pageNumber, pageSize, filters });
  const i = items;
  const exists = useCallback((p: IProduct) => (i ?? []).some((s) => s.productId === p.id), [i]);

  useEffect(() => {
    setIsButtonDisabled(!useSearch.itemSelected || exists(useSearch.itemSelected));
  }, [useSearch.itemSelected, items, exists]);

  const isLoading = useSearch.isLoading || isLoadingRequest;

  return (
    <Grid container size={{ xs: 12, md: 4 }} justifyContent={"center"} alignItems="center" my={2}>
      <Autocomplete
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        value={useSearch.itemSelected}
        onChange={(_, itemSelected) => useSearch.selectItem(itemSelected)}
        fullWidth
        isOptionEqualToValue={(option, value) => option.code === value.code}
        getOptionLabel={(option) => option.code || ""}
        options={data?.items || []}
        loading={isLoading}
        noOptionsText={messages.productNotFound}
        filterOptions={(options) => options}
        renderOption={(props, option) => (
          <li
            {...props}
            key={`${String(option.id ?? "noid")}-${String(option.code ?? option.title)}`}
          >
            {option.code}
            {option.title ? ` - ${option.title}` : ""}
          </li>
        )}
        renderInput={(params) => {
          const { InputProps, ...otherParams } = params;

          return (
            <MDInput
              {...otherParams}
              label={messages.productCodeLabel}
              variant="standard"
              onBlur={useSearch.clearSearch}
              onChange={(e) => useSearch.onSearch(e.target.value)}
              InputProps={{
                ...InputProps,
                endAdornment: (
                  <>
                    {isLoading && (
                      <CircularProgress color="inherit" sx={{ padding: 0 }} size={20} />
                    )}
                    {InputProps.endAdornment}
                    <IconButton
                      sx={{ padding: 0 }}
                      onClick={() => onConfirm(useSearch.itemSelected, setFieldValue)}
                      disabled={isButtonDisabled || !useSearch.itemSelected}
                    >
                      <AddIcon />
                    </IconButton>
                  </>
                ),
              }}
            />
          );
        }}
      />
    </Grid>
  );
};

export default ProductAutoComplete;
